export interface VersionInfo {
  version: string;
  buildTime: string;
  buildNumber: string;
  commitHash: string;
  environment: string;
}

class VersionService {
  private static instance: VersionService | undefined;
  private currentVersion: VersionInfo | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private listeners: Set<(version: VersionInfo) => void> = new Set();

  static getInstance(): VersionService {
    if (!VersionService.instance) {
      VersionService.instance = new VersionService();
    }
    return VersionService.instance;
  }

  async getCurrentVersion(): Promise<VersionInfo | null> {
    if (this.currentVersion) {
      return this.currentVersion;
    }

    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-store',
      });
      if (response.ok) {
        this.currentVersion = await response.json();
        return this.currentVersion;
      }
    } catch (error) {
      console.error('Error fetching current version:', error);
    }
    return null;
  }

  async checkForUpdate(): Promise<VersionInfo | null> {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-store',
      });
      if (response.ok) {
        const latestVersion = await response.json();
        return latestVersion;
      }
    } catch (error) {
      console.error('Error checking for update:', error);
    }
    return null;
  }

  hasUpdate(latestVersion: VersionInfo): boolean {
    if (!this.currentVersion) {
      // If we don't have a current version yet, set it and return false (no update)
      this.currentVersion = latestVersion;
      return false;
    }

    // Compare by buildTime or buildNumber
    // Don't update currentVersion here - we want to keep the original for comparison
    return (
      latestVersion.buildTime !== this.currentVersion.buildTime ||
      latestVersion.buildNumber !== this.currentVersion.buildNumber
    );
  }

  updateCurrentVersion(version: VersionInfo): void {
    this.currentVersion = version;
  }

  onVersionUpdate(callback: (version: VersionInfo) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  notifyListeners(version: VersionInfo): void {
    this.listeners.forEach((callback) => callback(version));
  }

  startPeriodicCheck(intervalMs: number = 5 * 60 * 1000): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    // Initial check
    this.checkForUpdate().then((latestVersion) => {
      if (latestVersion && this.hasUpdate(latestVersion)) {
        this.notifyListeners(latestVersion);
      }
    });

    // Periodic checks
    this.checkInterval = setInterval(() => {
      this.checkForUpdate().then((latestVersion) => {
        if (latestVersion && this.hasUpdate(latestVersion)) {
          this.notifyListeners(latestVersion);
        }
      });
    }, intervalMs);
  }

  stopPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/',
        });
        console.log('[Version Service] Service Worker registered:', registration);

        // Listen for service worker messages
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'VERSION_CHECK') {
            const latestVersion = event.data.version as VersionInfo;
            if (this.hasUpdate(latestVersion)) {
              this.notifyListeners(latestVersion);
            }
          }
        });

        return registration;
      } catch (error) {
        console.error('[Version Service] Service Worker registration failed:', error);
      }
    }
    return null;
  }

  async forceUpdate(): Promise<void> {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      // Send skip waiting message
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });

      // Wait a bit then reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } else {
      // Fallback: just reload
      window.location.reload();
    }
  }
}

export default VersionService.getInstance();
